import { useEffect, useState } from "react";
import { Card, KpiCard, ScostamentoRischio, SectionTitle, TornaHome } from "@/components/Kpi";
import { COLORS, ComparisonChart, PieBreakdown } from "@/components/Charts";
import { ConfrontoYtdAutonomo } from "@/components/corto/ConfrontoYtd";
import BudgetGauge from "@/components/BudgetGauge";
import { FILTRI_CORTO_VUOTI, FiltriCorto } from "@/components/corto/FiltriCorto";
import { StatoSync } from "@/components/corto/StatoSync";
import { ContoCartaChart } from "@/components/corto/ContoCartaChart";
import { ApprovatoDaLiquidareModal } from "@/components/corto/ApprovatoDaLiquidareModal";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { MESI, num, dateIt, eurShort as eur } from "@/lib/format";

const SERIE_PRODOTTO = {
  pezzi_caricato: "pratiche_caricate", caricato: "caricato",
  pezzi_liquidato: "pratiche_liquidate", liquidato: "liquidato",
};
/** schiarisce un colore esadecimale mescolandolo col bianco (0 = pieno, 1 = bianco) */
const schiarisci = (hex, q) => {
  const n = parseInt(hex.slice(1), 16);
  const mix = (c) => Math.round(c + (255 - c) * q);
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map((c) => mix(c).toString(16).padStart(2, "0")).join("")}`;
};

const chiaviProdotto = (base) => [
  { key: "garantito", name: "Garantito", color: base, stackId: "p",
    labelPosition: "top", labelStyle: { fill: base } },
  { key: "non_garantito", name: "Non garantito", color: schiarisci(base, 0.4), stackId: "p",
    labelStyle: { fill: "#FFFFFF" } },
  { key: "vivibanca", name: "Vivibanca", color: schiarisci(base, 0.72), stackId: "p",
    labelPosition: "top", labelStyle: { fill: base } },
];

const GRUPPI = [
  ["erogato", "Netto erogato"],
];

const qsPdf = (anno, filtri, ytd) => {
  const p = new URLSearchParams({ anno: String(anno) });
  Object.entries({ ...filtri, ...ytd }).forEach(([k, v]) => { if (v) p.set(k, v); });
  return p.toString();
};

const quota = (parte, tot) => (tot ? `${num(parte / tot * 100, 1)}% del totale` : "n/d");

export default function CanaleCorto() {
  const [dati, setDati] = useState(null);
  const [agenzie, setAgenzie] = useState([]);
  const [filtri, setFiltri] = useState({ ...FILTRI_CORTO_VUOTI });
  const [risorse, setRisorse] = useState([]);
  const [statoYtd, setStatoYtd] = useState({});
  const [errore, setErrore] = useState("");
  const [ricarica, setRicarica] = useState(0);
  const [approvato, setApprovato] = useState(false);
  const { isMaster } = useAuth();

  useEffect(() => {
    const qs = new URLSearchParams(Object.entries(filtri).filter(([, v]) => v)).toString();
    api.get(`/corto/dashboard${qs ? `?${qs}` : ""}`).then((r) => setDati(r.data))
      .catch(() => setErrore("Dati del canale corto non disponibili"));
  }, [filtri, ricarica]);

  useEffect(() => {
    api.get("/corto/agenzie").then((r) => setAgenzie(r.data.rows || [])).catch(() => setAgenzie([]));
    api.get("/corto/risorse").then((r) => setRisorse(r.data.rows || [])).catch(() => setRisorse([]));
  }, []);

  if (errore) return <Card className="p-8 text-sm text-slate-500" data-testid="corto-errore">{errore}</Card>;
  if (!dati) return <div className="text-sm text-slate-500" data-testid="corto-loading">Caricamento…</div>;

  const t = dati.totali;
  const p = dati.previsioni || {};
  const meseLbl = p.mese ? MESI[p.mese - 1].toLowerCase() : "mese";
  const prodotti = ["non_garantito", "garantito", "vivibanca"];
  const nomiProdotto = { non_garantito: "Non garantito", garantito: "Garantito", vivibanca: "Vivibanca" };
  const sommaProdotto = (serie, k) => serie.reduce((s, r) => s + (r[k] || 0), 0);

  return (
    <div data-testid="canale-corto-page" className="space-y-8">
      <TornaHome testId="btn-torna-scelta" />
      <SectionTitle
        sub={`Prestiti personali · ${dati.anno} · ${dati.fonte}${dati.ultima_sincronizzazione
          ? ` · aggiornato il ${dateIt(dati.ultima_sincronizzazione)}` : ""}`}
        right={<div className="flex items-center gap-3 flex-wrap">
          {isMaster && dati.fonte === "CRM Databeasy" && (
            <StatoSync ultima={dati.ultima_sincronizzazione} onSincronizzato={() => setRicarica((r) => r + 1)} />
          )}
          <a href={`/api/corto/report/dashboard.pdf?${qsPdf(dati.anno, filtri, statoYtd)}`}
            data-testid="btn-pdf-corto-dashboard"
            className="px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm
              hover:bg-[#001480] transition-colors duration-200">Scarica PDF</a>
        </div>}>
        Dashboard corto · agenzie
      </SectionTitle>

      <FiltriCorto valori={filtri} onChange={setFiltri} agenzie={agenzie} risorse={risorse} />


      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="corto-kpi-totali">
        <KpiCard label="Caricato totale" value={t.importo_caricato} kind="eur" accent="caricato"
          sub={`${num(t.pratiche_caricate)} pratiche`} testId="corto-kpi-caricato" />
        <KpiCard label="Caricato non garantito" value={t.caricato_non_garantito} kind="eur"
          accent="caricato" sub={quota(t.caricato_non_garantito, t.importo_caricato)} testId="corto-kpi-caricato-ng" />
        <KpiCard label="Caricato garantito" value={t.caricato_garantito} kind="eur"
          accent="caricato" sub={quota(t.caricato_garantito, t.importo_caricato)} testId="corto-kpi-caricato-g" />
        <KpiCard label="Caricato Vivibanca" value={t.caricato_vivibanca} kind="eur"
          accent="caricato" sub={quota(t.caricato_vivibanca, t.importo_caricato)} testId="corto-kpi-caricato-vb" />

        <KpiCard label="Liquidato totale" value={t.importo_liquidato} kind="eur" accent="liquidato"
          sub={`${num(t.pratiche_liquidate)} pratiche`} testId="corto-kpi-liquidato" />
        <KpiCard label="Liquidato non garantito" value={t.liquidato_non_garantito} kind="eur"
          accent="liquidato" sub={quota(t.liquidato_non_garantito, t.importo_liquidato)} testId="corto-kpi-liquidato-ng" />
        <KpiCard label="Liquidato garantito" value={t.liquidato_garantito} kind="eur"
          accent="liquidato" sub={quota(t.liquidato_garantito, t.importo_liquidato)} testId="corto-kpi-liquidato-g" />
        <KpiCard label="Liquidato Vivibanca" value={t.liquidato_vivibanca} kind="eur"
          accent="liquidato" sub={quota(t.liquidato_vivibanca, t.importo_liquidato)} testId="corto-kpi-liquidato-vb" />

        <KpiCard label="% raggiungimento budget liquidato" value={p.raggiungimento_perc} kind="quota"
          accent="previsione" sub={`Budget ${eur(p.budget_liquidato)}`}
          testId="corto-kpi-budget-liquidato" />
        <KpiCard label="Previsione chiusura anno · liquidato" value={p.proiezione_anno} kind="eurShort"
          accent="previsione" variance={p.scostamento_perc} sub="scostamento vs budget"
          testId="corto-kpi-previsione-anno-liquidato" />
        <KpiCard label={`Previsione chiusura ${meseLbl} · pratiche liquidate`} value={p.proiezione_mese_pratiche}
          accent="previsione" testId="corto-kpi-previsione-mese-liquidate" />
        <KpiCard label={`Previsione chiusura ${meseLbl} · importo liquidato`} value={p.proiezione_mese_importo}
          kind="eurShort" accent="previsione" testId="corto-kpi-previsione-mese-liquidato" />

        <KpiCard label="Premi assicurativi (CPI + Bundle)" value={t.premi} kind="eur" accent="sky"
          sub="importi liquidati" testId="corto-kpi-premi" />

        <KpiCard label="Redditività" value={t.redditivita} kind="quota" accent="positivo"
          testId="corto-kpi-redditivita" />
        <KpiCard label="Rischio" value={t.rischio} kind="quota"
          accent={t.rischio != null && t.rischio_target != null ? (t.rischio <= t.rischio_target ? "positivo" : "negativo") : "slate"}
          sub={<ScostamentoRischio rischio={t.rischio} target={t.rischio_target} />} testId="corto-kpi-rischio" />
        <KpiCard label="Approvato da liquidare" value={t.approvato_da_liquidare?.importo} kind="eur"
          accent="warning" sub={`${num(t.approvato_da_liquidare?.pratiche || 0)} pratiche approvate`}
          onClick={() => setApprovato(true)} testId="corto-kpi-approvato-da-liquidare" />
      </div>
      {approvato && <ApprovatoDaLiquidareModal anno={dati.anno} agenzia={filtri.agenzia || null}
        risorsa={filtri.risorsa || null}
        onClose={() => setApprovato(false)} />}

      {dati.confronto ? (
        <div className="space-y-4" data-testid="corto-confronto-anni">
          <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
            Confronto {dati.confronto.anno} vs {dati.confronto.anno_precedente}
          </div>
          {[["pezzi_caricato", "caricato"], ["caricato", "caricato"],
            ["pezzi_liquidato", "liquidato"], ["liquidato", "liquidato"]].map(([campo, tinta]) => (
            <div key={campo} className="space-y-4">
              <ComparisonChart height={320} testId={`corto-confronto-${campo}`}
                title={`${dati.confronto.titoli[campo]} · ${dati.confronto.anno_precedente} vs ${dati.confronto.anno}`}
                labelFormat={campo.startsWith("pezzi") ? "int" : "compact"}
                data={dati.confronto.serie[campo]}
                keys={[{ key: "precedente", name: String(dati.confronto.anno_precedente), color: COLORS.grigio },
                  { key: "corrente", name: String(dati.confronto.anno), color: COLORS[tinta] }]} />
              <ComparisonChart height={340} testId={`corto-prodotti-${campo}`}
                title={`${dati.confronto.titoli[campo]} ${dati.anno} · per prodotto`}
                labelFormat={campo.startsWith("pezzi") ? "int" : "compact"}
                data={dati[SERIE_PRODOTTO[campo]]} keys={chiaviProdotto(COLORS[tinta])} />
              {campo === "liquidato" ? (
                <ConfrontoYtdAutonomo agenzie={agenzie} testId="corto-ytd-liquidato"
                  extra={{ risorsa: filtri.risorsa }} onStato={setStatoYtd} />
              ) : null}
            </div>
          ))}
        </div>
      ) : null}


      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ComparisonChart testId="corto-chart-importi" title="Importi caricato vs liquidato · per mese"
          data={dati.caricato.map((r, i) => ({ label: r.label, caricato: r.totale,
            liquidato: dati.liquidato[i].totale }))}
          keys={[{ key: "caricato", name: "Caricato", color: COLORS.caricato },
            { key: "liquidato", name: "Liquidato", color: COLORS.liquidato }]} />
        <ComparisonChart testId="corto-chart-pratiche" title="Pratiche caricate vs liquidate"
          labelFormat="int"
          data={dati.pratiche_caricate.map((r, i) => ({ label: r.label, caricate: r.totale,
            liquidate: dati.pratiche_liquidate[i].totale }))}
          keys={[{ key: "caricate", name: "Caricate", color: COLORS.caricato },
            { key: "liquidate", name: "Liquidate", color: COLORS.liquidato }]} />
      </div>

      <div className="space-y-4">
          <ComparisonChart testId="corto-chart-ticket" title="Ticket medio · caricato, liquidato, netto erogato"
            type="line" labelFormat="int" data={dati.ticket}
            keys={[{ key: "caricato", name: "Caricato", color: COLORS.caricato },
              { key: "liquidato", name: "Liquidato", color: COLORS.liquidato },
              { key: "netto_erogato", name: "Netto erogato", color: COLORS.pos }]} />
          <ComparisonChart testId="corto-chart-premi" title="Premi assicurativi per mese"
            data={dati.premi} keys={[{ key: "totale", name: "Premi", color: COLORS.curr }]} />
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
            <div className="xl:col-span-2">
              <ComparisonChart testId="corto-chart-stati" title="Esiti pratiche per mese" labelFormat="int"
                data={dati.stati}
                keys={[{ key: "accettata", name: "Accettate", color: COLORS.pos, stackId: "s" },
                  { key: "attesa", name: "In attesa", color: COLORS.warn, stackId: "s" },
                  { key: "respinta", name: "Respinte", color: "#DC2626", stackId: "s" },
                  { key: "ritirata", name: "Ritirate", color: COLORS.grigio, stackId: "s" },
                  { key: "rinuncia", name: "Rinunce", color: "#A855F7", stackId: "s" }]} />
            </div>
            <PieBreakdown testId="corto-pie-caricato" title="Caricato per prodotto"
              data={prodotti.map((k) => ({ name: nomiProdotto[k], value: sommaProdotto(dati.caricato, k) }))} />
            <PieBreakdown testId="corto-pie-liquidato" title="Liquidato per prodotto"
              data={prodotti.map((k) => ({ name: nomiProdotto[k], value: sommaProdotto(dati.liquidato, k) }))} />
          </div>
      </div>

      <ContoCartaChart anno={dati.anno} agenzia={filtri.agenzia} escludi={filtri.escludi} />

      {dati.tachimetri?.length ? (
        <div data-testid="corto-tachimetri">
          <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">
            Obiettivi {dati.anno} · anno, quarter e mese in corso
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {dati.tachimetri.map((k) => (
              <BudgetGauge key={k.label} titolo={k.label} tipo="LIQUIDATO"
                sottotitolo={k.periodo === "mese" ? `proiezione mese vs budget · liquidato a oggi ${eur(k.consuntivo)}`
                  : `proiezione ${k.periodo} vs budget`}
                budget={k.budget} proiezione={k.valore} scostamentoPerc={k.scostamento_perc}
                testId={`corto-tachimetro-${k.periodo}-${k.categoria}`} />
            ))}
          </div>
        </div>
      ) : null}

      {GRUPPI.map(([gruppo, titolo]) => {
        const blocchi = dati.kpi.filter((k) => k.gruppo === gruppo);
        if (!blocchi.length) return null;
        return (
          <div key={gruppo} data-testid={`corto-gruppo-${gruppo}`}>
            <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">
              {titolo}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {blocchi.map((k) => (
                <BudgetGauge key={k.label} titolo={k.label} tipo="LIQUIDATO"
                  sottotitolo={k.periodo === "mese" ? `proiezione mese vs budget · liquidato a oggi ${eur(k.consuntivo)}`
                    : "proiezione liquidato vs obiettivo"}
                  budget={k.budget}
                  proiezione={k.valore} scostamentoPerc={k.scostamento_perc}
                  testId={`corto-tachimetro-${k.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
